import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatRippleModule } from '@angular/material/core';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslatePipe } from '@ngx-translate/core';
import { SnackbarService } from '@siat-fe/shared';
import { BehaviorSubject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { CustomValidators, InputFieldComponent } from '../../form';
import { AnagraficaSearchDTO, PatientDTO } from './search-patient.model';
import { SearchPatientService } from './search-patient.service';

@UntilDestroy()
@Component({
  selector: 'siat-search-patient-dialog',

  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    TranslatePipe,
    MatDividerModule,
    MatRippleModule,
    InputFieldComponent,
  ],
  templateUrl: './search-patient-dialog.component.html',
  styleUrl: './search-patient-dialog.component.scss',
})
export class SearchPatientDialog implements OnInit {
  /**
   * The formgroup.
   */
  form: FormGroup;

  /**
   * The search results.
   */
  searchResults = new BehaviorSubject<PatientDTO[]>([]);

  /**
   * The selected patient.
   */
  selectedPatient: PatientDTO | null = null;

  /**
   * the injections.
   */
  private fb = inject(FormBuilder);
  private searchPatientService = inject(SearchPatientService);
  private router = inject(Router);
  public dialogRef = inject(MatDialogRef<SearchPatientDialog>);
  snackbarService = inject(SnackbarService);

  /**
   * @inheritdoc
   */
  ngOnInit(): void {
    this.form = this.fb.group(
      {
        nome: [''],
        cognome: [''],
        codiceFiscale: ['', CustomValidators.invalidCf],
      },
      {
        validators: this.nameOrFiscalCodeRequiredValidator(),
      }
    );
  }

  /**
   * Search patients.
   */
  searchPatients(): void {
    const searchDTO = this.anagraficaSearchDTO;
    this.clearSelectedPatient();

    this.searchPatientService
      .findFromAPSS(searchDTO)
      .pipe(
        untilDestroyed(this),
        tap(patients => {
          if (patients.warningMessage) {
            this.snackbarService.showDangerSnackbar(patients.warningMessage);
          }
          this.searchResults.next(patients?.anagraficaList);
        })
      )
      .subscribe();
  }

  /**
   * @returns {AnagraficaSearchDTO}
   */
  get anagraficaSearchDTO(): AnagraficaSearchDTO {
    const { nome, cognome, codiceFiscale } = this.form.value;

    return { nome, cognome, codiceFiscale } as AnagraficaSearchDTO;
  }

  /**
   * On click reset.
   */
  clearFields(): void {
    this.form.reset();
    this.clearSelectedPatient();
    this.searchResults.next([]);
  }

  /**
   * On click add new patient.
   */
  onNewPatient(): void {
    this.dialogRef.close();

    this.router.navigate(['/home/nuova-autosegnalazione/']);
  }

  /**
   * On patient selected.
   */
  onPatientSelected(patient: PatientDTO): void {
    this.selectedPatient = patient;
  }

  /**
   * On patient key down.
   */
  onPatientKeyDown(event: KeyboardEvent, patient: PatientDTO): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopImmediatePropagation();

      this.onPatientSelected(patient);
    }
  }

  checkLength(controlValue: string) {
    if (controlValue?.length <= 2 && controlValue?.length >= 1) {
      return true;
    }
    return false;
  }

  /**
   * On submit.
   */
  onSubmit(): void {
    if (!this.form.valid || !this.selectedPatient) {
      return;
    }

    if (this.selectedPatient.fromAsuit) {
      this.searchPatientService
        .fetchAddPatient(this.selectedPatient)
        .subscribe((returnedPatient: PatientDTO) => {
          this.navigateToSegnalazione(returnedPatient.id);
        });
    } else {
      this.navigateToSegnalazione(this.selectedPatient.id);
    }
  }

  clearSelectedPatient(): void {
    this.selectedPatient = null;
  }

  /**
   * Validate form if name and surname are required or fiscal code.
   */
  nameOrFiscalCodeRequiredValidator(): ValidatorFn {
    return (formGroup: AbstractControl): ValidationErrors | null => {
      const nome = formGroup.get('nome') as FormControl<string>;
      const cognome = formGroup.get('cognome') as FormControl<string>;
      const codiceFiscale = formGroup.get(
        'codiceFiscale'
      ) as FormControl<string>;

      if (!nome || !cognome || !codiceFiscale) return null;

      const isFiscalCodeValid = codiceFiscale.value && codiceFiscale.valid;
      const isNameAndSurnameValid =
        nome.value && cognome.value && nome.valid && cognome.valid;

      if (isNameAndSurnameValid || isFiscalCodeValid) {
        codiceFiscale.setErrors(null);
        return null;
      } else {
        return { fieldsRequired: true };
      }
    };
  }

  private navigateToSegnalazione(patientId: number): void {
    this.dialogRef.close();
    this.router.navigate(['/home/nuova-autosegnalazione/' + patientId]);
  }
}
